import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { X, Plus, Trash2 } from 'lucide-react';
import API_ENDPOINTS from '../../config/api';

const FIELD_TYPES = [
  { value: 'text', label: 'Text (Short answer)' },
  { value: 'textarea', label: 'Textarea (Long answer)' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'number', label: 'Number' },
  { value: 'select', label: 'Dropdown (Select one)' },
  { value: 'radio', label: 'Radio buttons (Select one)' },
  { value: 'checkbox', label: 'Checkbox (Yes/No)' }
];

const RSVPFieldEditor = ({ field, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    field_type: 'text',
    field_label: '',
    placeholder_text: '',
    help_text: '',
    is_required: false,
    options: [''],
    sort_order: 0
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (field) {
      setFormData({
        field_type: field.field_type || 'text',
        field_label: field.field_label || '',
        placeholder_text: field.placeholder_text || '',
        help_text: field.help_text || '',
        is_required: field.is_required || false,
        options: field.options || [''],
        sort_order: field.sort_order || 0
      });
    }
  }, [field]);

  const needsOptions = ['select', 'radio'].includes(formData.field_type);
  const supportsPlaceholder = ['text', 'email', 'phone', 'number'].includes(formData.field_type);

  const handleAddOption = () => {
    setFormData({
      ...formData,
      options: [...formData.options, '']
    });
  };

  const handleRemoveOption = (index) => {
    const newOptions = formData.options.filter((_, i) => i !== index);
    setFormData({
      ...formData,
      options: newOptions.length > 0 ? newOptions : ['']
    });
  };

  const handleOptionChange = (index, value) => {
    const newOptions = [...formData.options];
    newOptions[index] = value;
    setFormData({
      ...formData,
      options: newOptions
    });
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.field_label.trim()) {
      newErrors.field_label = 'Field label is required';
    }

    if (needsOptions) {
      const nonEmptyOptions = formData.options.filter(opt => opt.trim() !== '');
      if (nonEmptyOptions.length === 0) {
        newErrors.options = 'At least one option is required for select/radio fields';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) {
      return;
    }

    setSaving(true);

    try {
      const payload = {
        field: {
          ...formData,
          options: needsOptions ? formData.options.filter(opt => opt.trim() !== '') : null
        }
      };

      if (field) {
        // Update existing field
        payload.field.id = field.id;
        const response = await axios.put(API_ENDPOINTS.ADMIN_RSVP_FIELDS, payload);
        
        if (response.data.success) {
          alert('Field updated successfully!');
          onSave();
        } else {
          if (response.data.errors) {
            setErrors(response.data.errors);
          } else {
            alert(response.data.message || 'Error updating field');
          }
        }
      } else {
        // Create new field
        const response = await axios.post(API_ENDPOINTS.ADMIN_RSVP_FIELDS, payload);
        
        if (response.data.success) {
          alert('Field created successfully!');
          onSave();
        } else {
          if (response.data.errors) {
            setErrors(response.data.errors);
          } else {
            alert(response.data.message || 'Error creating field');
          }
        }
      }
    } catch (err) {
      console.error('Error saving field:', err);
      alert('Error saving field');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal">
      <div className="modal-content modal-large">
        <div className="modal-header">
          <h2>{field ? 'Edit Field' : 'Add New Field'}</h2>
          <button className="close-btn" onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        <div className="modal-body">
          <div className="form-group">
            <label>Field Type *</label>
            <select
              value={formData.field_type}
              onChange={(e) => setFormData({ ...formData, field_type: e.target.value })}
              disabled={!!field}
            >
              {FIELD_TYPES.map(type => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
            {field && <small>Field type cannot be changed after creation</small>}
          </div>

          <div className="form-group">
            <label>Field Label *</label>
            <input
              type="text"
              value={formData.field_label}
              onChange={(e) => setFormData({ ...formData, field_label: e.target.value })}
              placeholder="e.g., Dietary Restrictions"
            />
            {errors.field_label && <span className="error-text">{errors.field_label}</span>}
          </div>

          {supportsPlaceholder && (
            <div className="form-group">
              <label>Placeholder Text</label>
              <input
                type="text"
                value={formData.placeholder_text}
                onChange={(e) => setFormData({ ...formData, placeholder_text: e.target.value })}
                placeholder="e.g., Enter your dietary needs"
              />
            </div>
          )}

          <div className="form-group">
            <label>Help Text</label>
            <textarea
              value={formData.help_text}
              onChange={(e) => setFormData({ ...formData, help_text: e.target.value })}
              rows="2"
              placeholder="Optional help text shown below the field"
            />
          </div>

          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={formData.is_required}
                onChange={(e) => setFormData({ ...formData, is_required: e.target.checked })}
              />
              Required field
            </label>
          </div>

          {needsOptions && (
            <div className="form-group">
              <label>Options * <small>(one per line)</small></label>
              {formData.options.map((option, index) => (
                <div key={index} className="option-row">
                  <input
                    type="text"
                    value={option}
                    onChange={(e) => handleOptionChange(index, e.target.value)}
                    placeholder={`Option ${index + 1}`}
                  />
                  {formData.options.length > 1 && (
                    <button
                      type="button"
                      className="btn-icon-danger"
                      onClick={() => handleRemoveOption(index)}
                    >
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>
              ))}
              <button type="button" className="btn-secondary btn-sm" onClick={handleAddOption}>
                <Plus size={16} /> Add Option
              </button>
              {errors.options && <span className="error-text">{errors.options}</span>}
            </div>
          )}
        </div>

        <div className="modal-actions">
          <button className="btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Field'}
          </button>
          <button className="btn-secondary" onClick={onClose} disabled={saving}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default RSVPFieldEditor;
